import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

import { CommentsPostCard_Post } from '../../fragments/CommentsPostCard_Post'
import {
  CommentLastReply_LastReply,
  CommentMediaAsset_Asset,
} from './commentLastReply'

/**
 * The full comment shape the card tree consumes through `CommentContext`. It
 * selects every field the surrounding card / list / context / reply surfaces
 * read off a comment, so `CommentData` derives from one place instead of the
 * codegen `CommentData` union.
 *
 * The comment-level fields are a flat selection, not a composition of the
 * colocated component fragments: an unmasked fragment that re-selects `assets`
 * produces an intersection type the shared fixtures don't satisfy.
 *
 * `post` carries both a flat selection and a spread of
 * {@link CommentsPostCard_Post} because it has two kinds of consumer. The card
 * tree reads fields like `post.text` straight off `comment.post`, so those stay
 * selected here; the post miniature reads the masked fragment through
 * `readFragment`, and the spread adds only that reference.
 *
 * Lives in a `fragments/` directory because codegen's document scanner only
 * globs `.tsx`, `use*.ts`, and `fragments/*.ts` — a plain `.ts` elsewhere
 * wouldn't be collected, and producers spreading `...CommentData_Comment`
 * would fail document validation.
 */
export const CommentData_Comment = graphql(
  /* GraphQL */ `
    fragment CommentData_Comment on CommentData @_unmask {
      ... on BaseComment {
        __typename
        id
        text
        contentType
        publishedAt
        postPublishedAt
        status
        parentId
        channelId
        serviceType
        isOwnReply
        isDeleted
        publicUrl
        organizationId
        metadata {
          ... on GoogleBusinessCommentMetadata {
            userRating
          }
        }
        reactions {
          type
        }
        assets {
          ...CommentMediaAsset_Asset
        }
        author {
          username
          name
          avatar
        }
        lastReplies {
          ...CommentLastReply_LastReply
        }
        post {
          id
          text
          dueAt
          channelService
          externalLink
          assets {
            __typename
            mimeType
            thumbnail
            source
            type
            ... on ImageAsset {
              image {
                __typename
                altText
                width
                height
                isAnimated
              }
            }
            ... on VideoAsset {
              video {
                width
                height
              }
            }
          }
          tags {
            name
            color
          }
          channel {
            id
            service
            name
            displayName
          }
          ...CommentsPostCard_Post
        }
      }
      ... on CommentFromThirdParty {
        labels
        suggestions {
          text
          source
        }
        sentimentAnalysis {
          type
        }
      }
      ... on CommentFromBuffer {
        publishingStatus {
          status
        }
      }
    }
  `,
  [CommentLastReply_LastReply, CommentMediaAsset_Asset, CommentsPostCard_Post],
)

/** The full comment shape provided through `CommentContext`. */
export type CommentData = FragmentOf<typeof CommentData_Comment>

export type { LastReply } from './commentLastReply'
